import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/api-client';
import getColumnClass from '../../utils/getColumnClass';
import CardSkeleton from '../Common/CardSkeleton';
import FilterBar from '../Common/FilterBar';
import GridControls from '../Common/GridControls';
import ProductCard from './ProductCard';
const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sortCriteria, setSortCriteria] = useState('0');
  const productContainerRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [gridSize, setGridSize] = useState(() => {
    const storeGridZize = localStorage.getItem('UniqueGridSize');
    return storeGridZize ? parseInt(storeGridZize, 10) : 4;
  });
  useEffect(() => {
    localStorage.setItem('UniqueGridSize', gridSize.toString());
  }, [gridSize]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    apiClient
      .get('/category')
      .then((res) => {
        setCategories(res.data.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  const fetchProducts = useCallback(
    async (currentPage) => {
      try {
        setIsFetching(true); // Set fetching status to true when request starts
        const res = await apiClient.post('/products', { page: currentPage });
        const { data: newProducts, total_pages } = res.data;
        setProducts((prevProducts) => {
          const uniqueNewProducts = newProducts.filter(
            (newProduct) =>
              !prevProducts.some(
                (product) => product.product_id === newProduct.product_id
              )
          );
          const updatedProducts = [...prevProducts, ...uniqueNewProducts];
          setDisplayedProducts(sortProducts(updatedProducts)); // Update displayed products after sorting
          return updatedProducts;
        });
        setTotalPages(total_pages); // Update total pages from response
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      } finally {
        setIsFetching(false); // Set fetching status to false when request completes
      }
    },
    [sortCriteria]
  );
  useEffect(() => {
    fetchProducts(page);
  }, [fetchProducts, page]);
  useEffect(() => {
    setDisplayedProducts(sortProducts([...products]));
  }, [sortCriteria, products]);
  const handleScroll = useCallback(() => {
    if (
      !isFetching && // Check if not already fetching
      productContainerRef.current &&
      productContainerRef.current.getBoundingClientRect().bottom <=
        window.innerHeight + 100 &&
      page < totalPages
    ) {
      console.log(`Adding page: ${page + 1} to queue`); // Debug log
      setPage((prevPage) => prevPage + 1);
    }
  }, [isFetching, page, totalPages]);
  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 200);
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [handleScroll]);
  // Debounce function to limit the rate of function execution
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  const handleSortChange = (event) => {
    setSortCriteria(event.target.value);
  };
  const sortProducts = (products) => {
    switch (sortCriteria) {
      case 'featured':
        return products.sort((a, b) => b.is_featured - a.is_featured);
      case 'new_arrival':
        return products.sort(
          (a, b) => new Date(b.time_stamp) - new Date(a.time_stamp)
        );
      case 'biggest_discount':
        return products.sort((a, b) => {
          const maxDiscountA = Math.max(
            ...a.varients.map((v) => v.discount_percent)
          );
          const maxDiscountB = Math.max(
            ...b.varients.map((v) => v.discount_percent)
          );
          return maxDiscountB - maxDiscountA;
        });
      case 'low_to_high':
        return products.sort((a, b) => {
          const minPriceA = Math.min(...a.varients.map((v) => v.final_price));
          const minPriceB = Math.min(...b.varients.map((v) => v.final_price));
          return minPriceA - minPriceB;
        });
      case 'high_to_low':
        return products.sort((a, b) => {
          const minPriceA = Math.min(...a.varients.map((v) => v.final_price));
          const minPriceB = Math.min(...b.varients.map((v) => v.final_price));
          return minPriceB - minPriceA;
        });
      default:
        return products;
    }
  };
  return (
    <>
      <FilterBar
        handleSortChange={handleSortChange}
        products={products}
        categories={categories}
      />
      <GridControls
        gridSize={gridSize}
        setGridSize={setGridSize}
        windowWidth={windowWidth}
      />
      {/* Products Grid */}
      <div className="row justify-content-center" ref={productContainerRef}>
        {error && <em className="form_error text-danger">{error}</em>}
        {isLoading && (
          <CardSkeleton
            gridSize={gridSize}
            windowWidth={windowWidth}
            cards={8}
          />
        )}
        {displayedProducts &&
          displayedProducts.map((product) => (
            <div
              className={`${getColumnClass(gridSize, windowWidth)} p-2`}
              key={product.product_id}
            >
              <ProductCard product={product} />
            </div>
          ))}
        {isFetching && (
          <CardSkeleton
            gridSize={gridSize}
            windowWidth={windowWidth}
            cards={8}
          />
        )}
      </div>
    </>
  );
};
export default ProductList;
